import { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AudioWaveform,
  Copy,
  Check,
  ExternalLink,
  Play,
  Activity,
  FolderOpen,
  AlertTriangle,
  Mic,
  Volume2,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { NeMoInstanceDetails, NeMoModel, mockNeMoInstanceDetails } from '@/mock/gpu-instance';

interface NeMoDetailPanelProps {
  taskId: string;
}

const getTypeIcon = (type: NeMoModel['type']) => {
  switch (type) {
    case 'asr':
      return <Mic className="h-4 w-4 text-blue-500" />;
    case 'tts':
      return <Volume2 className="h-4 w-4 text-green-500" />;
    case 'nlp':
      return <MessageSquare className="h-4 w-4 text-purple-500" />;
    case 'multimodal':
      return <Sparkles className="h-4 w-4 text-yellow-500" />;
  }
};

const getTypeBadge = (type: NeMoModel['type']) => {
  const labels: Record<NeMoModel['type'], string> = {
    asr: 'Speech-to-Text',
    tts: 'Text-to-Speech',
    nlp: 'NLP',
    multimodal: 'Multimodal',
  };
  const colors: Record<NeMoModel['type'], string> = {
    asr: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    tts: 'bg-green-500/10 text-green-600 border-green-500/30',
    nlp: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    multimodal: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  };
  return <Badge variant="outline" className={colors[type]}>{labels[type]}</Badge>;
};

const getStatusBadge = (status: NeMoModel['status']) => {
  switch (status) {
    case 'training':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Play className="h-3 w-3 mr-1" /> Training
        </Badge>
      );
    case 'inference':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Activity className="h-3 w-3 mr-1" /> Inference
        </Badge>
      );
    case 'idle':
      return (
        <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
          Idle
        </Badge>
      );
  }
};

const NeMoDetailPanel = ({ taskId }: NeMoDetailPanelProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const details: NeMoInstanceDetails = useMemo(() => mockNeMoInstanceDetails(taskId), [taskId]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trainingModels = details.models.filter((m) => m.status === 'training').length;
  const inferenceModels = details.models.filter((m) => m.status === 'inference').length;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-cyan-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <AudioWaveform className="h-6 w-6 text-cyan-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  NVIDIA NeMo
                  <Badge
                    variant="outline"
                    className={
                      details.status === 'active'
                        ? 'bg-green-500/10 text-green-600 border-green-500/30'
                        : 'bg-gray-500/10 text-gray-600'
                    }
                  >
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  End-to-end platform for speech and NLP AI
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono">
                v{details.nemoVersion}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => window.open(details.notebookUrl, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Jupyter
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-600">{trainingModels}</div>
            <div className="text-sm text-muted-foreground">Training</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-green-600">{inferenceModels}</div>
            <div className="text-sm text-muted-foreground">Inference</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{details.models.length}</div>
            <div className="text-sm text-muted-foreground">Total Models</div>
          </CardContent>
        </Card>
      </div>

      {/* Models */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Speech & NLP Models</CardTitle>
          <CardDescription>Active training and inference models</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {details.models.map((model) => (
            <Card key={model.name} className="bg-muted/30">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getTypeIcon(model.type)}
                    <div>
                      <div className="font-medium">{model.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {getTypeBadge(model.type)}
                        <span className="text-xs text-muted-foreground font-mono">{model.checkpoint}</span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(model.status)}
                </div>

                {model.status === 'training' && model.metrics.epoch !== undefined && (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        Epoch {model.metrics.epoch}/{model.metrics.totalEpochs}
                      </span>
                    </div>
                    <Progress
                      value={(model.metrics.epoch! / model.metrics.totalEpochs!) * 100}
                      className="h-2"
                    />
                  </div>
                )}

                {Object.keys(model.metrics).length > 0 && (
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    {model.metrics.wer !== undefined && (
                      <div className="p-2 bg-background rounded text-center">
                        <div className="text-muted-foreground text-xs">WER</div>
                        <div className="font-mono font-medium text-green-600">
                          {(model.metrics.wer * 100).toFixed(1)}%
                        </div>
                      </div>
                    )}
                    {model.metrics.cer !== undefined && (
                      <div className="p-2 bg-background rounded text-center">
                        <div className="text-muted-foreground text-xs">CER</div>
                        <div className="font-mono font-medium text-green-600">
                          {(model.metrics.cer * 100).toFixed(1)}%
                        </div>
                      </div>
                    )}
                    {model.metrics.bleu !== undefined && (
                      <div className="p-2 bg-background rounded text-center">
                        <div className="text-muted-foreground text-xs">BLEU</div>
                        <div className="font-mono font-medium text-green-600">
                          {model.metrics.bleu.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {model.metrics.perplexity !== undefined && (
                      <div className="p-2 bg-background rounded text-center">
                        <div className="text-muted-foreground text-xs">Perplexity</div>
                        <div className="font-mono font-medium">
                          {model.metrics.perplexity.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Paths */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Workspace Paths
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Audio Dataset Path</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono pr-12">
                {details.datasetPath}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.datasetPath, 'dataset')}
              >
                {copiedField === 'dataset' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Checkpoints Path</Label>
            <div className="relative">
              <pre className="bg-muted p-3 rounded-lg text-sm font-mono pr-12">
                {details.checkpointsPath}
              </pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-1 right-1 h-8"
                onClick={() => handleCopy(details.checkpointsPath, 'checkpoints')}
              >
                {copiedField === 'checkpoints' ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data. In production,
          speech and NLP metrics will update in real-time.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default NeMoDetailPanel;
